import { useState, useCallback, useEffect } from "react";
import { notify } from "@/ui/chakra/notify";
import { useConfirmToggle } from "@/ui/modals/useConfirm";
import { PageContainer } from "@/ui/containers/PageContainer";
import { useGetExternalProducts } from "../useCases/useGetLocalProduct";
import { STOCK_FILTERS, type StockFilter } from "@/utils/stockFilters";
import { TableFilters } from "@/ui/filters/TableFilters";
import {
  countActiveFilters,
  getOption,
  type FilterValues,
} from "@/ui/filters/filterValues";
import { useStorageFilterField } from "@/core/storages/useCases/useStorageFilterField";
import { useUpdateLocalProduct } from "../useCases/useUpdateLocalProduct";
import type { LocalProduct, LocalProductInput } from "../types/LocalProduct";
import { Button, HStack, Image, Text } from "@chakra-ui/react";
import { Table } from "@/ui/tables/Table";
import { getStoredPageSize } from "@/ui/tables/pageSize";
import { FilterChips } from "@/ui/tables/FilterChips";
import type { TableProps } from "@/ui/tables/Table";
import { LuArchive, LuFileSpreadsheet, LuPencil, LuPlus } from "react-icons/lu";
import { getStockStatus } from "@/utils/getStatusStock";
import { CreateLocalProductModal } from "../components/CreateLocalProductModal";
import { EditLocalProductModal } from "../components/EditLocalProductModal";
import type { Pagination } from "@/core/common/types";
import ErrorStockAlert from "@/core/products/components/ErrorStockAlert";
import LocalProductInventoryModal from "../components/LocalProductInventoryModal";
import { useAuth } from "@/shared/session";
import { useDebounce } from "@/core/common/hooks/useDebounce";
import { downloadLocalProductReport } from "../utils/downloadLocalProductReport";
import { downloadExternalProductsExcel } from "../utils/downloadExternalProductsExcel";
import { formatPrice } from "@/utils/formatPrice";
import qr from "query-string";
import { CreatorCell } from "@/core/vouchers/Columns";

const getColumns = ({
  handleToggleEnabled,
  handlePrices,
}: {
  handleToggleEnabled: (localProduct: LocalProduct) => void;
  handlePrices: (localProduct: LocalProduct) => void;
}): TableProps<LocalProduct>["headers"] => [
  {
    label: "Imagen",
    itemLabel: (item) =>
      item.imageUrl ? (
        <a href={item.imageUrl} target="_blank" rel="noopener noreferrer">
          <Image
            src={item.imageUrl}
            alt={item.product.description}
            boxSize="44px"
            objectFit="contain"
            borderRadius="md"
            cursor="pointer"
            bg="bg.subtle"
          />
        </a>
      ) : (
        <Image
          src="/placeholder-image.svg"
          alt="Sin imagen"
          boxSize="44px"
          objectFit="contain"
          borderRadius="md"
          bg="bg.subtle"
        />
      ),
  },
  {
    label: "Código",
    itemLabel: (item) => <Text fontWeight="semibold">{item.code}</Text>,
    sortable: true,
    sortKey: "code",
  },
  {
    label: "Descripción",
    itemLabel: (item) => item.product.description,
    sortable: true,
    sortKey: "description",
  },
  {
    label: "Unidad",
    itemLabel: (item) => item.product.unit,
  },
  {
    label: "Stock",
    itemLabel: (item: LocalProduct) =>
      getStockStatus(item.product?.stock, item.product?.minimumStock),
    sortable: true,
    sortKey: "stock",
  },
  {
    label: "Precio promedio ponderado",
    itemLabel: (item: LocalProduct) =>
      item.weightedAveragePrice != null
        ? formatPrice(item.weightedAveragePrice)
        : "---",
    sortable: true,
    sortKey: "weightedAveragePrice",
  },
  {
    label: "Valor total inventario",
    itemLabel: (item: LocalProduct) =>
      item.totalWeightedPrice != null
        ? formatPrice(item.totalWeightedPrice)
        : "---",
    sortable: true,
    sortKey: "totalWeightedPrice",
  },
  {
    label: "Inventario",
    itemLabel: (item: LocalProduct) => (
      <Button size="sm" variant="outline" onClick={() => handlePrices(item)}>
        Ver inventario
      </Button>
    ),
  },
  {
    label: "Comentario",
    itemLabel: "comment",
  },
  {
    label: "Conversor",
    itemLabel: (item) => item.product.converter,
  },
  {
    label: "Tipo de conversión",
    itemLabel: (item: LocalProduct) => item.product?.converterType?.name ?? "",
  },
  {
    label: "Stock mínimo",
    itemLabel: (item) => item.product.minimumStock,
  },
  {
    label: "Stock máximo",
    itemLabel: "maximumStock",
  },
  {
    label: "Bodega",
    itemLabel: (item: LocalProduct) => item.product.storage.name ?? "",
  },
  {
    label: "Creado por",
    itemLabel: (item) => <CreatorCell name={item.creator?.name} />,
  },
  {
    label: "Estado",
    itemLabel: (item: LocalProduct) => (
      <Button
        variant="ghost"
        size="sm"
        colorPalette={item.enabled ? "gray" : "green"}
        color={item.enabled ? "fg.muted" : undefined}
        onClick={() => handleToggleEnabled(item)}
      >
        {item.enabled ? "Deshabilitar" : "Habilitar"}
      </Button>
    ),
  },
];

export const LocalProductPage = () => {
  const { session } = useAuth();

  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: getStoredPageSize(),
  });

  const [order, setOrder] = useState<{
    sort?: string;
    order?: string;
  }>({
    sort: undefined,
    order: undefined,
  });

  const [showDisabled, setShowDisabled] = useState(false);
  const [isOpenPricesModal, setIsOpenPricesModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState<string>();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [stockFilter, setStockFilter] = useState<StockFilter>("all");
  const stockStatus = stockFilter === "all" ? undefined : stockFilter;
  const storageField = useStorageFilterField();
  const [extraFilters, setExtraFilters] = useState<FilterValues>({});
  const storageId = getOption(extraFilters, "storage")?.value;

  const {
    data: localProducts = [],
    refetch,
    isPending,
    pagination: metaProduct,
  } = useGetExternalProducts({
    page: pagination.page,
    pageSize: pagination.pageSize,
    showDisabled,
    ...order,
    zoneId: session?.zone ?? undefined,
    searchTerm: debouncedSearchTerm,
    stockStatus,
    storageId,
  });

  const { mutate: updateLocalProduct } = useUpdateLocalProduct();

  const [errorRelatedVouchers, setErrorRelatedVouchers] = useState<
    | {
        voucherCode: string;
        usedStock: number;
        productCode: string;
      }[]
    | null
  >(null);

  const [selectedLocalProduct, setSelectedLocalProduct] =
    useState<LocalProduct | null>(null);

  const [isOpenEditLocalProductModal, setIsOpenEditLocalProductModal] =
    useState(false);

  const [isOpenCreateLocalProductModal, setIsOpenCreateLocalProductModal] =
    useState(false);

  const confirmToggle = useConfirmToggle();

  const handleToggleEnabled = useCallback(
    async (data: LocalProduct) => {
      if (!session?.zone) return;
      const ok = await confirmToggle(
        data.enabled,
        `el producto ${data.product.description}`,
      );
      if (!ok) return;
      const payload: LocalProductInput = {
        ...data,
        enabled: !data.enabled,
        description: data.product.description,
        unit: data.product.unit,
        location: data.product.location,
        stock: data.product.stock,
        minimumStock: data.product.minimumStock,
        converter: data.product.converter,
        storageId: data.product.storageId,
        zoneId: session?.zone,
        converterTypeId: data.product.converterTypeId,
      };

      updateLocalProduct(
        { id: data.id, updateLocalProducts: payload },
        {
          onSuccess: () => {
            notify.success(
              payload.enabled
                ? "Producto habilitado"
                : "Producto deshabilitado",
            );
            refetch();
          },
          onError: (error) => {
            const relatedVouchers =
              error.response?.data?.error?.relatedVouchers;
            if (relatedVouchers) {
              setErrorRelatedVouchers(relatedVouchers);
              return;
            }
            notify.error("No se pudo actualizar el producto", error);
          },
        },
      );
    },
    [updateLocalProduct, refetch, session?.zone, confirmToggle],
  );

  const handlePrices = useCallback(
    (localProduct: LocalProduct) => {
      setSelectedLocalProduct(localProduct);
      setIsOpenPricesModal(true);
    },
    [setSelectedLocalProduct],
  );

  const handleGetReport = useCallback(async (localProduct: LocalProduct) => {
    await downloadLocalProductReport(
      localProduct.id,
      `report-${localProduct.id}.xlsx`,
    );
  }, []);

  const handleOnSortChange = (sort: string, order: string) => {
    setOrder({ sort, order });
  };

  const [exporting, setExporting] = useState(false);

  const handleExportExcel = async () => {
    const params = qr.stringify({
      zoneId: session?.zone,
      showDisabled,
      searchTerm: debouncedSearchTerm,
      sort: order.sort,
      order: order.order,
      stockStatus,
      storageId,
    });
    setExporting(true);
    try {
      await notify.run(() => downloadExternalProductsExcel(params), {
        loading: "Preparando el Excel de productos…",
        error: "No se pudo exportar a Excel",
      });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm, stockFilter, storageId, showDisabled]);

  return (
    <PageContainer
      display="flex"
      flexDir="column"
      gap={4}
      title="Productos locales"
      description="Gestiona los productos locales."
      actions={
        <>
          <Button
            colorPalette="brand"
            onClick={() => setIsOpenCreateLocalProductModal(true)}
          >
            <LuPlus />
            Crear producto local
          </Button>
        </>
      }
    >
      <Table
        searchPlaceholder="Buscar por código o descripción…"
        pageSize={pagination.pageSize}
        onChangePageSize={(pageSize) => setPagination({ page: 1, pageSize })}
        isLoading={isPending}
        page={pagination.page}
        hasActiveFilters={
          stockFilter !== "all" || countActiveFilters(extraFilters) > 0
        }
        onClearFilters={() => {
          setStockFilter("all");
          setExtraFilters({});
        }}
        filters={
          <HStack gap={1.5} flexWrap="wrap">
            <FilterChips
              options={STOCK_FILTERS}
              value={stockFilter}
              onChange={setStockFilter}
              counts={metaProduct?.countByStockStatus}
            />
            <TableFilters
              fields={[storageField]}
              value={extraFilters}
              onChange={setExtraFilters}
            />
          </HStack>
        }
        emptyDescription="Los productos locales son los que se gestionan en las bodegas de esta zona."
        emptyAction={
          <Button
            colorPalette="brand"
            size="sm"
            onClick={() => setIsOpenCreateLocalProductModal(true)}
          >
            Crear producto local
          </Button>
        }
        data={localProducts}
        headers={getColumns({
          handleToggleEnabled,
          handlePrices,
        })}
        extractItemKey={(item: LocalProduct) => item.id}
        onShowDisabledChange={(value) => setShowDisabled(value)}
        showDisabledCheck
        onSortChange={handleOnSortChange}
        onSearch={(value) => setSearchTerm(value)}
        pages={metaProduct?.totalPages}
        totalItems={metaProduct?.count}
        itemsLabel="productos"
        onChangePage={(page) =>
          setPagination((prev) => ({ ...prev, page: page + 1 }))
        }
        actions={[
          {
            label: "Editar",
            onClick: (item) => {
              setSelectedLocalProduct(item as LocalProduct);
              setIsOpenEditLocalProductModal(true);
            },
            icon: <LuPencil size={18} />,
          },
          {
            label: "Ver ingresos y salidas",
            onClick: (item) => {
              handleGetReport(item as LocalProduct);
            },
            icon: <LuArchive size={18} />,
          },
        ]}
        headerActions={
          <Button
            size="sm"
            variant="outline"
            colorPalette="brand"
            onClick={handleExportExcel}
            loading={exporting}
          >
            <LuFileSpreadsheet />
            Exportar a Excel
          </Button>
        }
        hiddenColumnsKey="localProducts"
      />

      <CreateLocalProductModal
        isOpen={isOpenCreateLocalProductModal}
        onClose={() => setIsOpenCreateLocalProductModal(false)}
      />

      {selectedLocalProduct && (
        <EditLocalProductModal
          isOpen={isOpenEditLocalProductModal}
          onClose={() => {
            setIsOpenEditLocalProductModal(false);
            setSelectedLocalProduct(null);
          }}
          selectedLocalProduct={selectedLocalProduct}
          onError={(error) => {
            setErrorRelatedVouchers(error?.relatedVouchers);
          }}
        />
      )}
      <ErrorStockAlert
        isOpen={!!errorRelatedVouchers}
        type="localProduct"
        onClose={() => setErrorRelatedVouchers(null)}
        relatedVouchers={errorRelatedVouchers || []}
      />
      {selectedLocalProduct && (
        <LocalProductInventoryModal
          isOpen={isOpenPricesModal}
          onClose={() => {
            setIsOpenPricesModal(false);
            setSelectedLocalProduct(null);
          }}
          localProduct={selectedLocalProduct}
        />
      )}
    </PageContainer>
  );
};
